/**
 * Redux slice for Postgres-backed facet (filter sidebar) counts.
 *
 * Manages facet datasets:
 * - `data` — master auth-status tab counts (top-level tabs).
 * - `facet_results` — dynamic filter sidebar facet counts based on active filters.
 * - `unique_filter_options` — facet counts for unique filter options (no assignee scope).
 * @module postgresFacetsCountsSlice
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getFacetsCountFromPostgres,
  GetFacetsCountFromPostgresParams,
} from "../../../api/postgreSQL/postCalls/getFacetsCountFromPostgres";
import { PostgresFacetCountResponseModel } from "../../../data-model/postgresFacetCountResponseModel";

/**
 * Arguments accepted by {@link getUniqueFilterOptions}.
 *
 * Same shape as {@link GetFacetsCountFromPostgresParams} except `assigned_to`,
 * `created_at_start`, and `created_at_end` are omitted so the request is
 * org-wide facet cardinality (not scoped to the current user or a date window).
 */
export type GetUniqueFilterOptionsParams = Omit<
  GetFacetsCountFromPostgresParams,
  "assigned_to" | "created_at_start" | "created_at_end"
>;

/**
 * State shape for Postgres facet counts: tab totals, assignee-scoped sidebar
 * facets, and org-wide data used to build distinct filter values.
 */
interface PostgresFacetCountsState {
  /** Tab-level counts (master auth status). */
  data?: PostgresFacetCountResponseModel;
  /** Dynamic sidebar facet counts reflecting current filter selections. */
  facet_results?: PostgresFacetCountResponseModel;
  /** Facet counts for building unique filter options (org-wide, not assignee-scoped). */
  unique_filter_options?: PostgresFacetCountResponseModel;
  loading?: boolean;
  error?: string;
  is_facet_results_loading?: boolean;
  /** True while {@link getUniqueFilterOptions} is in flight. */
  is_unique_filter_options_loading?: boolean;
}

const initialState: PostgresFacetCountsState = {
  data: undefined,
  facet_results: undefined,
  unique_filter_options: undefined,
  is_facet_results_loading: false,
  is_unique_filter_options_loading: false,
  loading: false,
  error: undefined,
};

export const postgresFacetCountsSlice = createSlice({
  name: "postgresFacetCounts",
  initialState,
  reducers: {
    setPostgresFacetCounts: (
      state,
      action: PayloadAction<PostgresFacetCountResponseModel>,
    ) => {
      state.data = action.payload;
      state.loading = false;
      state.error = undefined;
    },
    setPostgresFacetResults: (
      state,
      action: PayloadAction<PostgresFacetCountResponseModel>,
    ) => {
      state.facet_results = action.payload;
      state.is_facet_results_loading = false;
      state.error = undefined;
    },
    setIsFacetResultsLoading: (state, action: PayloadAction<boolean>) => {
      state.is_facet_results_loading = action.payload;
      state.facet_results = undefined;
      state.error = undefined;
    },
    /** Commits the unique-filter-options response and clears `is_unique_filter_options_loading`. */
    setUniqueFilterOptions: (
      state,
      action: PayloadAction<PostgresFacetCountResponseModel>,
    ) => {
      state.unique_filter_options = action.payload;
      state.is_unique_filter_options_loading = false;
      state.error = undefined;
    },
    /**
     * Toggles loading for {@link getUniqueFilterOptions}. When `true`, clears any
     * stale `unique_filter_options` and the slice `error` (mirrors {@link setIsFacetResultsLoading}).
     */
    setIsUniqueFilterOptionsLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.is_unique_filter_options_loading = action.payload;
      state.unique_filter_options = undefined;
      state.error = undefined;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      state.data = undefined;
      state.error = undefined;
    },
    /** Records a failure for any Postgres facet fetch using the shared `error` field. */
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
      state.data = undefined;
      state.is_unique_filter_options_loading = false;
      state.unique_filter_options = undefined;
    },
    resetPostgresFacetCounts: (state) => {
      state.data = undefined;
      state.loading = false;
      state.error = undefined;
      state.facet_results = undefined;
      state.is_facet_results_loading = false;
      state.unique_filter_options = undefined;
      state.is_unique_filter_options_loading = false;
    },
  },
});

export const {
  setPostgresFacetCounts,
  setLoading,
  setError,
  resetPostgresFacetCounts,
  setPostgresFacetResults,
  setIsFacetResultsLoading,
  setUniqueFilterOptions,
  setIsUniqueFilterOptionsLoading,
} = postgresFacetCountsSlice.actions;

/**
 * Thunk: fetches facet counts grouped by master_auth_status for the tab bar.
 *
 * @param payload - Org, assignee, and optional facet parameters.
 */
export const fetchCountsByMasterAuthStatusByPostgres =
  (payload: GetFacetsCountFromPostgresParams) =>
  async (dispatch, getState: any) => {
    const { postgresFacetCounts } = getState();
    if (postgresFacetCounts.loading) {
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await getFacetsCountFromPostgres(payload);
      dispatch(
        setPostgresFacetCounts(response as PostgresFacetCountResponseModel),
      );
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

/**
 * Thunk: loads org-wide facet value counts used to populate filter dropdowns / chips.
 *
 * Skips if `is_unique_filter_options_loading` is already true. On success, dispatches
 * {@link setUniqueFilterOptions}; on failure, {@link setError} (also clears tab `data`
 * and unique-option state because `error` is shared across this slice).
 *
 * @param payload - `org_id`, optional `facet_attributes`, `facet_filters`, `timezone_offset`.
 *   Does not send `assigned_to`, `created_at_start`, or `created_at_end`.
 */
export const getUniqueFilterOptions =
  (payload: GetUniqueFilterOptionsParams) =>
  async (dispatch, getState: any) => {
    const { postgresFacetCounts } = getState();
    if (postgresFacetCounts.is_unique_filter_options_loading) {
      return;
    }
    dispatch(setIsUniqueFilterOptionsLoading(true));
    try {
      const response = await getFacetsCountFromPostgres({
        org_id: payload.org_id,
        facet_attributes: payload.facet_attributes,
        facet_filters: payload.facet_filters,
        timezone_offset:
          payload.timezone_offset ?? new Date().getTimezoneOffset(),
      });
      dispatch(
        setUniqueFilterOptions(response as PostgresFacetCountResponseModel),
      );
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

/**
 * Thunk: fetches dynamic sidebar facet counts reflecting the user's active filters.
 * Defaults timezone_offset to the browser's offset when not provided.
 *
 * @param payload - Org, assignee, facet attributes, and active facet filters.
 */
export const fetchPostgresFacetResults =
  (payload: GetFacetsCountFromPostgresParams) =>
  async (dispatch, getState: any) => {
    const { postgresFacetCounts } = getState();
    if (postgresFacetCounts.is_facet_results_loading) {
      return;
    }
    dispatch(setIsFacetResultsLoading(true));
    dispatch(setLoading(true));
    try {
      const response = await getFacetsCountFromPostgres({
        org_id: payload.org_id,
        facet_attributes: payload.facet_attributes,
        assigned_to: payload.assigned_to,
        facet_filters: payload.facet_filters,
        timezone_offset:
          payload?.timezone_offset ?? new Date().getTimezoneOffset(),
      });
      dispatch(
        setPostgresFacetResults(response as PostgresFacetCountResponseModel),
      );
    } catch (error) {
      dispatch(setError(error as string));
    }
  };
export default postgresFacetCountsSlice.reducer;
